import { useState } from "react";
import { LEAD_STATUSES, STATUS_ACCENT, fmtMoney } from "@/lib/leadConstants";
import { GripVertical, Eye, Phone, Mail } from "lucide-react";

export default function LeadKanban({ items, onMove, onView }) {
  const [dragId, setDragId] = useState(null);
  const [overStatus, setOverStatus] = useState(null);

  const drop = (status) => {
    setOverStatus(null);
    const lead = items.find((l) => l.id === dragId);
    setDragId(null);
    if (lead && lead.status !== status) onMove(lead.id, status);
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-4" data-testid="lead-kanban">
      {LEAD_STATUSES.map((status) => {
        const cards = items.filter((l) => l.status === status);
        const total = cards.reduce((a, b) => a + (b.budget || 0), 0);
        return (
          <div
            key={status}
            data-testid={`kanban-col-${status.replace(/\s+/g, "-").toLowerCase()}`}
            onDragOver={(e) => { e.preventDefault(); setOverStatus(status); }}
            onDragLeave={() => setOverStatus(null)}
            onDrop={() => drop(status)}
            className={`flex min-h-[460px] w-[280px] shrink-0 flex-col rounded-xl border-t-4 bg-slate-50/70 ${STATUS_ACCENT[status]} ${overStatus === status ? "ring-2 ring-primary ring-offset-2" : ""}`}
          >
            <div className="flex items-center justify-between px-4 pt-3">
              <div>
                <h3 className="font-display text-sm font-semibold text-secondary">{status}</h3>
                <p className="text-xs text-muted-foreground">{fmtMoney(total)}</p>
              </div>
              <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-600 shadow-sm">{cards.length}</span>
            </div>
            <div className="flex-1 space-y-2.5 p-3">
              {cards.map((l) => (
                <div
                  key={l.id}
                  draggable
                  onDragStart={() => setDragId(l.id)}
                  data-testid={`kanban-card-${l.id}`}
                  className="group cursor-grab rounded-lg border border-border bg-white p-3 shadow-sm transition-shadow duration-200 hover:shadow-md active:cursor-grabbing"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-secondary">{l.full_name}</p>
                      <p className="font-mono text-[10px] text-slate-400">{l.lead_code}</p>
                    </div>
                    <GripVertical className="h-4 w-4 shrink-0 text-slate-300" />
                  </div>
                  {l.interested_course && <p className="mt-1 truncate text-xs text-muted-foreground">{l.interested_course}</p>}
                  <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
                    {l.mobile && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {l.mobile}</span>}
                  </div>
                  <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
                    <span className="text-xs font-semibold text-primary">{l.budget ? fmtMoney(l.budget) : "—"}</span>
                    <button onClick={() => onView(l.id)} data-testid={`kanban-view-${l.id}`} className="rounded p-1 text-slate-400 opacity-0 transition-opacity hover:bg-slate-100 hover:text-primary group-hover:opacity-100">
                      <Eye className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              {cards.length === 0 && <p className="pt-6 text-center text-xs text-slate-400">Drop leads here</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
